// Estados de carga, vacío y error reutilizables.
import type { ReactNode } from 'react';
import { esApiError } from '../api/http';

export function PantallaCarga({ texto = 'Cargando…' }: { texto?: string }) {
  return (
    <div className="pantalla-carga" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span>{texto}</span>
    </div>
  );
}

export function Esqueleto({ alto = 16, ancho = '100%', radio = 6 }: { alto?: number; ancho?: number | string; radio?: number }) {
  return <span className="esqueleto" style={{ height: alto, width: ancho, borderRadius: radio }} aria-hidden="true" />;
}

export function EsqueletoTabla({ filas = 6, columnas = 6 }: { filas?: number; columnas?: number }) {
  return (
    <div className="esqueleto-tabla" role="status" aria-label="Cargando datos">
      {Array.from({ length: filas }).map((_, i) => (
        <div className="esqueleto-tabla__fila" key={i}>
          {Array.from({ length: columnas }).map((__, j) => (
            <Esqueleto key={j} alto={14} ancho={j === 0 ? '70%' : `${50 + ((i * 7 + j * 13) % 40)}%`} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function EsqueletoTarjetas({ n = 4, alto = 96 }: { n?: number; alto?: number }) {
  return (
    <div className="rejilla-kpi" role="status" aria-label="Cargando indicadores">
      {Array.from({ length: n }).map((_, i) => (
        <div className="tarjeta" key={i} style={{ minHeight: alto }}>
          <Esqueleto alto={12} ancho="45%" />
          <div style={{ height: 12 }} />
          <Esqueleto alto={26} ancho="70%" />
        </div>
      ))}
    </div>
  );
}

export function Vacio({ titulo, children, accion }: { titulo: string; children?: ReactNode; accion?: ReactNode }) {
  return (
    <div className="vacio">
      <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
        <rect x="6" y="9" width="28" height="22" rx="4" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M6 16h28" stroke="currentColor" strokeWidth="2" />
      </svg>
      <p className="vacio__titulo">{titulo}</p>
      {children && <p className="vacio__texto">{children}</p>}
      {accion}
    </div>
  );
}

export function PanelError({ error, alReintentar }: { error: unknown; alReintentar?: () => void }) {
  if (esApiError(error) && error.status === 403) {
    return (
      <div className="panel-error panel-error--permiso" role="alert">
        <p className="panel-error__titulo">Sin permiso</p>
        <p>{error.detalle || 'Su rol no tiene acceso a esta información.'}</p>
        <p className="texto-tenue">Si necesita acceso, solicítelo a Gerencia indicando el rol requerido.</p>
      </div>
    );
  }
  if (esApiError(error) && error.status === 404) {
    return (
      <div className="panel-error" role="alert">
        <p className="panel-error__titulo">No encontrado</p>
        <p>{error.detalle}</p>
      </div>
    );
  }
  return (
    <div className="panel-error" role="alert">
      <p className="panel-error__titulo">No fue posible cargar la información</p>
      <p>{esApiError(error) ? error.detalle : 'Ocurrió un error inesperado.'}</p>
      {alReintentar && (
        <button className="btn btn--secundario" onClick={alReintentar}>
          Reintentar
        </button>
      )}
    </div>
  );
}

/** Mensaje de error de una acción (400/403/409/422) con su regla. */
export function ErrorAccion({ error }: { error: unknown }) {
  if (!error) return null;
  const titulo = esApiError(error)
    ? error.status === 403
      ? 'Sin permiso'
      : error.status === 422
        ? 'Bloqueado por una regla de negocio'
        : error.status === 409
          ? error.codigo === 'conflicto_version'
            ? 'Conflicto de versión'
            : error.codigo === 'transicion_invalida'
              ? 'Cambio no permitido'
              : 'Ya existe o no se puede repetir'
          : 'No se pudo completar'
    : 'No se pudo completar';
  return (
    <div className="aviso aviso--error" role="alert">
      <div>
        <strong>{titulo}</strong>
        {esApiError(error) && error.regla && <span className="etiqueta-regla">{error.regla}</span>}
      </div>
      <p>{esApiError(error) ? error.detalle : String(error)}</p>
    </div>
  );
}
